import React, { useEffect, useRef, useCallback, useState } from 'react';
import './KeyboardNavigation.css';

/**
 * KeyboardNavigation component for enhanced keyboard navigation
 * Based on ArbiWin design system and WCAG guidelines
 *
 * @param {Object} props - KeyboardNavigation properties
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.direction - Navigation direction: 'horizontal', 'vertical', 'both', 'grid'
 * @param {boolean} props.wrap - Whether navigation wraps around
 * @param {boolean} props.roving - Whether to use roving tabindex
 * @param {string} props.initialFocus - Selector for initial focus
 * @param {Function} props.onNavigate - Navigation event handler
 * @param {Function} props.onActivate - Activation event handler
 * @param {Object} props.keyMap - Custom key mappings
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} KeyboardNavigation component
 */
const KeyboardNavigation = function KeyboardNavigation({
  children,
  direction = 'both',
  wrap = true,
  roving = false,
  initialFocus,
  onNavigate,
  onActivate,
  keyMap = {},
  className = '',
  testId
}) {

  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [navigableElements, setNavigableElements] = useState([]);

  const defaultKeyMap = {
    next: ['ArrowDown', 'ArrowRight'],
    previous: ['ArrowUp', 'ArrowLeft'],
    first: ['Home'],
    last: ['End'],
    activate: ['Enter', ' '],
    ...keyMap
  };

  // Filter key map based on direction
  const getDirectionalKeyMap = function() {
    switch (direction) {
      case 'horizontal':
        return {
          ...defaultKeyMap,
          next: ['ArrowRight'],
          previous: ['ArrowLeft']
        };
      case 'vertical':
        return {
          ...defaultKeyMap,
          next: ['ArrowDown'],
          previous: ['ArrowUp']
        };
      case 'grid':
        return {
          ...defaultKeyMap,
          nextRow: ['ArrowDown'],
          previousRow: ['ArrowUp'],
          nextColumn: ['ArrowRight'],
          previousColumn: ['ArrowLeft']
        };
      default:
        return defaultKeyMap;
    }
  };

  const keyMappings = getDirectionalKeyMap();

  const navigationClasses = [
    'arbiwin-keyboard-navigation',
    'arbiwin-keyboard-navigation--' + direction,
    wrap ? 'arbiwin-keyboard-navigation--wrap' : '',
    roving ? 'arbiwin-keyboard-navigation--roving' : '',
    className
  ].filter(Boolean).join(' ');

  // Update tab indices for roving tabindex
  const updateTabIndices = function(elements, activeIndex) {
    elements.forEach(function(element, index) {
      element.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
    });
  };

  // Get navigable elements
  const getNavigableElements = useCallback(function() {
    if (!containerRef.current) return [];

    const selector = roving 
      ? '[data-keyboard-navigable], button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      : '[data-keyboard-navigable]';

    return Array.from(containerRef.current.querySelectorAll(selector))
      .filter(function(element) {
        return (
          !element.disabled &&
          element.offsetWidth > 0 &&
          element.offsetHeight > 0 &&
          window.getComputedStyle(element).visibility !== 'hidden'
        );
      });
  }, [roving]);

  // Update navigable elements when children change
  useEffect(function() {
    const updateElements = function() {
      const elements = getNavigableElements();
      setNavigableElements(elements);

      // Set initial focus
      if (initialFocus && elements.length > 0) {
        const initialElement = containerRef.current?.querySelector(initialFocus);
        if (initialElement) {
          const index = elements.indexOf(initialElement);
          if (index !== -1) {
            setCurrentIndex(index);
            if (roving) {
              updateTabIndices(elements, index);
            }
          }
        }
      }
    };

    updateElements();

    // Use MutationObserver to detect changes
    const observer = new MutationObserver(updateElements);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'tabindex', 'data-keyboard-navigable']
      });
    }

    return function() {
      observer.disconnect();
    };
  }, [children, getNavigableElements, initialFocus, roving]);

  // Get grid width for grid navigation
  const getGridWidth = function() {
    if (!containerRef.current || direction !== 'grid') return 1;

    const container = containerRef.current;
    const computedStyle = window.getComputedStyle(container);
    const gridTemplateColumns = computedStyle.gridTemplateColumns;

    if (gridTemplateColumns && gridTemplateColumns !== 'none') {
      return gridTemplateColumns.split(' ').length;
    }

    // Fallback: calculate based on element positions
    if (navigableElements.length < 2) return 1;

    const firstElement = navigableElements[0];
    const firstRect = firstElement.getBoundingClientRect();
    
    let width = 1;
    for (let i = 1; i < navigableElements.length; i++) {
      const elementRect = navigableElements[i].getBoundingClientRect();
      if (Math.abs(elementRect.top - firstRect.top) < 5) {
        width++;
      } else {
        break;
      }
    }

    return width;
  };

  // Navigate to specific index
  const navigateTo = useCallback(function(index) {
    if (navigableElements.length === 0) return;

    let newIndex = index;

    if (wrap) {
      if (newIndex < 0) {
        newIndex = navigableElements.length - 1;
      } else if (newIndex >= navigableElements.length) {
        newIndex = 0;
      }
    } else {
      newIndex = Math.max(0, Math.min(navigableElements.length - 1, newIndex));
    }

    setCurrentIndex(newIndex);
    
    const targetElement = navigableElements[newIndex];
    if (targetElement) {
      if (roving) {
        updateTabIndices(navigableElements, newIndex);
      }
      
      targetElement.focus();

      if (onNavigate) {
        onNavigate({
          element: targetElement,
          index: newIndex,
          direction,
          previousIndex: currentIndex
        });
      }
    }
  }, [navigableElements, currentIndex, wrap, roving, direction, onNavigate]);

  // Handle keyboard events
  useEffect(function() {
    const handleKeyDown = function(event) {
      if (!containerRef.current?.contains(event.target)) return;

      const key = event.key;
      
      // Check if any key mapping matches
      let action = null;
      Object.entries(keyMappings).forEach(function([actionName, keys]) {
        if (keys.includes(key)) {
          action = actionName;
        }
      });

      if (!action) return;

      event.preventDefault();
      event.stopPropagation();

      switch (action) {
        case 'next':
        case 'nextColumn':
          navigateTo(currentIndex + 1);
          break;
        case 'previous':
        case 'previousColumn':
          navigateTo(currentIndex - 1);
          break;
        case 'nextRow':
          if (direction === 'grid') {
            // Implement grid navigation logic
            const gridWidth = getGridWidth();
            navigateTo(currentIndex + gridWidth);
          }
          break;
        case 'previousRow':
          if (direction === 'grid') {
            const gridWidth = getGridWidth();
            navigateTo(currentIndex - gridWidth);
          }
          break;
        case 'first':
          navigateTo(0);
          break;
        case 'last':
          navigateTo(navigableElements.length - 1);
          break;
        case 'activate':
          const activeElement = navigableElements[currentIndex];
          if (activeElement) {
            if (onActivate) {
              onActivate({
                element: activeElement,
                index: currentIndex,
                event
              });
            } else {
              // Default activation behavior
              if (activeElement.click) {
                activeElement.click();
              } else if (activeElement.tagName === 'A' && activeElement.href) {
                window.location.href = activeElement.href;
              }
            }
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return function() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigateTo, currentIndex, navigableElements, keyMappings, direction, onActivate]);

  // Initialize roving tabindex
  useEffect(function() {
    if (roving && navigableElements.length > 0) {
      updateTabIndices(navigableElements, currentIndex);
    }
  }, [roving, navigableElements, currentIndex]);

  return (
    <div
      ref={containerRef}
      className={navigationClasses}
      data-keyboard-direction={direction}
      data-keyboard-wrap={wrap}
      data-keyboard-roving={roving}
      data-testid={testId || 'arbiwin-keyboard-navigation'}
    >
      {children}
    </div>
  );
};

/**
 * NavigableItem component for keyboard navigable items
 *
 * @param {Object} props - NavigableItem properties
 * @param {React.ReactNode} props.children - Child components
 * @param {Function} props.onActivate - Activation handler
 * @param {boolean} props.disabled - Whether item is disabled
 * @param {string} props.role - ARIA role
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} NavigableItem component
 */
const NavigableItem = function NavigableItem({
  children,
  onActivate,
  disabled = false,
  role = 'button',
  className = ''
}) {

  const itemRef = useRef(null);

  const itemClasses = [
    'arbiwin-navigable-item',
    disabled ? 'arbiwin-navigable-item--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const handleClick = function(event) {
    if (disabled) return;
    
    if (onActivate) {
      onActivate(event);
    }
  };

  const handleKeyDown = function(event) {
    if (disabled) return;
    
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  };

  return (
    <div
      ref={itemRef}
      className={itemClasses}
      role={role}
      tabIndex={disabled ? -1 : 0}
      data-keyboard-navigable
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
    >
      {children}
    </div>
  );
};

/**
 * useKeyboardNavigation hook for keyboard navigation utilities
 *
 * @param {Object} options - Hook options
 * @returns {Object} Keyboard navigation utilities
 */
const useKeyboardNavigation = function(options = {}) {
  const {
    direction = 'both',
    wrap = true,
    keyMap = {}
  } = options;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [elements, setElements] = useState([]);
  const containerRef = useRef(null);

  const navigate = useCallback(function(newIndex) {
    if (elements.length === 0) return;

    let targetIndex = newIndex;

    if (wrap) {
      if (targetIndex < 0) {
        targetIndex = elements.length - 1;
      } else if (targetIndex >= elements.length) {
        targetIndex = 0;
      }
    } else {
      targetIndex = Math.max(0, Math.min(elements.length - 1, targetIndex));
    }

    setCurrentIndex(targetIndex);
    
    if (elements[targetIndex]) {
      elements[targetIndex].focus();
    }
  }, [elements, wrap]);

  const navigateNext = useCallback(function() {
    navigate(currentIndex + 1);
  }, [navigate, currentIndex]);

  const navigatePrevious = useCallback(function() {
    navigate(currentIndex - 1);
  }, [navigate, currentIndex]);

  const navigateFirst = useCallback(function() {
    navigate(0);
  }, [navigate]);

  const navigateLast = useCallback(function() {
    navigate(elements.length - 1);
  }, [navigate, elements.length]);

  return {
    containerRef,
    currentIndex,
    elements,
    navigate,
    navigateNext,
    navigatePrevious,
    navigateFirst,
    navigateLast,
    setElements
  };
};

KeyboardNavigation.displayName = 'KeyboardNavigation';
NavigableItem.displayName = 'NavigableItem';

export default KeyboardNavigation;
export { NavigableItem, useKeyboardNavigation };
